import React, { useMemo, CSSProperties, Suspense } from 'react';
import { Translate, useGetList } from 'react-admin';
import {
  useMediaQuery,
  Theme,
  Skeleton,
  Card,
  CardHeader,
  CardContent,
} from '@mui/material';
import { subDays, startOfDay } from 'date-fns';

import Welcome from './Welcome';
import NbNewOrders from './NbNewOrders';
import PendingOrders from './PendingOrders';
import NewCustomers from './NewCustomers';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';

import { Order } from '../types';
import CardWithIcon from './CardWithIcon';

interface OrderStats {
  revenue: number;
  nbNewOrders: number;
  pendingOrders: Order[];
}

interface State {
  nbNewOrders?: number;
  pendingOrders?: Order[];
  recentOrders?: Order[];
  revenue?: string;
}

const styles = {
  flex: { display: 'flex' },
  flexColumn: { display: 'flex', flexDirection: 'column' },
  leftCol: { flex: 1, marginRight: '0.5em' },
  rightCol: { flex: 1, marginLeft: '0.5em' },
  singleCol: { marginBottom: '1em' },
};

const Spacer = () => <span style={{ width: '1em' }} />;
const VerticalSpacer = () => <span style={{ height: '1em' }} />;

const OrderChart = React.lazy(() => import('./OrderChart'));

import { useEffect, useState } from 'react';

interface OrdersResponse {
  data: any[];
  allOrders: number;
  activeOrders: number;
}

const API_URL = import.meta.env.VITE_API_URL;

export const useOrdersStats = () => {
  const [allOrders, setAllOrders] = useState<number>(0);
  const [activeOrders, setActiveOrders] = useState<number>(0);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/orders`)
      .then((res) => res.json())
      .then((json: OrdersResponse) => {
        setOrders(json.data || []);
        setAllOrders(json.allOrders || 0);
        setActiveOrders(json.activeOrders || 0);
      })
      .finally(() => setLoading(false));
  }, []);

  return { orders, allOrders, activeOrders, loading };
};

const Dashboard = () => {
  const { orders, allOrders, activeOrders } = useOrdersStats();

  const isXSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));
  const aMonthAgo = useMemo(() => subDays(startOfDay(new Date()), 30), []);

  const aggregation = useMemo<State>(() => {
    if (!orders || orders.length === 0) return {};
    const aggregations = orders
      .filter((order) => order.status !== 'cancelled')
      .reduce(
        (stats: OrderStats, order) => {
          if (order.status !== 'cancelled') {
            stats.revenue += order.total;
            stats.nbNewOrders++;
          }
          if (order.status === 'ordered') {
            stats.pendingOrders.push(order);
          }
          return stats;
        },
        {
          revenue: 0,
          nbNewOrders: 0,
          pendingOrders: [],
        },
      );
    return {
      recentOrders: orders,
      revenue: aggregations.revenue.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
      nbNewOrders: aggregations.nbNewOrders,
      pendingOrders: aggregations.pendingOrders,
    };
  }, [orders]);

  const { nbNewOrders, pendingOrders, revenue, recentOrders } = aggregation;
  return isXSmall ? (
    <div>
      <div style={styles.flexColumn as CSSProperties}>
        <Welcome />
        <VerticalSpacer />
        <NbNewOrders />
        <VerticalSpacer />
        <PendingOrders />
      </div>
    </div>
  ) : isSmall ? (
    <div style={styles.flexColumn as CSSProperties}>
      <div style={styles.singleCol}>
        <Welcome />
      </div>
      <div style={styles.flex}>
        <Spacer />
        <NbNewOrders value={nbNewOrders} />
      </div>
      <div style={styles.singleCol}>
        <PendingOrders />
      </div>
    </div>
  ) : (
    <>
      <Welcome />
      <div style={styles.flex}>
        <div style={styles.leftCol}>
          <div style={{ display: 'flex', gap: 10 }}>
            <CardWithIcon
              to="/"
              icon={ShoppingCartIcon}
              title="Замовлення"
              subtitle={allOrders}
            />
            <CardWithIcon
              to="/"
              icon={ShoppingCartIcon}
              title="Активні замовлення"
              subtitle={activeOrders}
            />
          </div>
          <div style={styles.singleCol}>
            <PendingOrders />
          </div>
        </div>
        <div style={styles.rightCol}>
          <div style={styles.flex}>
            <NewCustomers />
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
